import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { 
  Institution, 
  Organization,
  fetchUserOrganizations, 
  updateCurrentInstitution,
  updateCurrentOrganization,
  clearOrganizationCache,
  clearGroupsCache
} from '@/api/endpoints';
import { useQueryClient } from 'react-query';
import { toast } from '@/components/ui/use-toast';

interface OrganizationContextType {
  organizations: Organization[];
  currentOrganization: Organization | null;
  currentInstitution: Institution | null;
  institutions: Institution[]; // Add institutions to context
  isLoading: boolean;
  isLoadingInstitutions: boolean; // Add separate loading state for institutions
  error: string | null;
  noOrganizations: boolean;
  noInstitutions: boolean; // Add no institutions state
  setCurrentOrganization: (org: Organization) => void;
  setCurrentInstitution: (institution: Institution | null) => void;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

// Module-level guard: persists across React StrictMode unmount/remount cycles
// (useRef would reset on remount, causing a second fetchUserOrganizations() call
//  which sets isLoading=true and causes AppShell to briefly hide the page content,
//  unmounting page components and their React Query hooks, resulting in a double load)
let _orgFetchInitiated = false;

export const OrganizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { keycloak, initialized } = useKeycloak();
  const queryClient = useQueryClient();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [currentInstitution, setCurrentInstitution] = useState<Institution | null>(null);
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingInstitutions, setIsLoadingInstitutions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noOrganizations, setNoOrganizations] = useState(false);
  const [noInstitutions, setNoInstitutions] = useState(false);
  const isFetched = useRef(false);

  // Load and validate current institution from localStorage when organization changes
  // Note: Institutions are now loaded from the organizations endpoint above
  // This useEffect just handles cleanup when no organization is selected
  useEffect(() => {
    if (!currentOrganization) {
      setCurrentInstitution(null);
      setInstitutions([]);
      setNoInstitutions(false);
      localStorage.removeItem('currentInstitution');
    }
  }, [currentOrganization]);

  useEffect(() => {
    if (!initialized || !keycloak.authenticated) {
      return;
    }

    // Use both the module-level guard (survives StrictMode remount)
    // and the instance ref (prevents re-runs within the same mount)
    if (_orgFetchInitiated || isFetched.current) return;
    _orgFetchInitiated = true;
    isFetched.current = true;

    const fetchOrganizations = async () => {
      setIsLoading(true);
      setIsLoadingInstitutions(true); // Start loading institutions too
      try {
        // Use the new backend endpoint to fetch user organizations
        const response = await fetchUserOrganizations();
        
        if (!response.success) {
          console.error('Failed to fetch organizations:', response.error);
          setError(response.error || 'Failed to load organizations');
          setNoOrganizations(true);
          setCurrentOrganization(null);
          return;
        }
        
        const orgs = response.organizations || [];
        
        console.log("Fetched organizations from backend:", orgs);
        console.log("Backend currentOrganization:", response.currentOrganization);
        
        if (orgs.length > 0) {
          setOrganizations(orgs);
          setNoOrganizations(false);
          
          // If org is stored in localStorage, use it
          const savedOrgId = localStorage.getItem('currentOrganizationId');
          const savedOrg = savedOrgId ? orgs.find((org: Organization) => org.id === savedOrgId) : null;
          
          let selectedOrg: Organization;
          
          if (savedOrg) {
            selectedOrg = savedOrg;
            setCurrentOrganization(savedOrg);
          } else if (response.currentOrganization) {
            // Use backend's current organization (find by ID since response.currentOrganization is a string)
            const currentOrg = orgs.find((org: Organization) => org.id === response.currentOrganization);
            if (currentOrg) {
              selectedOrg = currentOrg;
              setCurrentOrganization(currentOrg);
              localStorage.setItem('currentOrganizationId', currentOrg.id);
            } else {
              // Fallback to first org if current org ID not found
              selectedOrg = orgs[0];
              setCurrentOrganization(orgs[0]);
              localStorage.setItem('currentOrganizationId', orgs[0].id);
            }
          } else {
            // Default to first org if none selected
            selectedOrg = orgs[0];
            setCurrentOrganization(orgs[0]);
            localStorage.setItem('currentOrganizationId', orgs[0].id);
          }
          
          // Now handle institutions from the selected organization
          const orgInstitutions = selectedOrg.institutions || [];
          setInstitutions(orgInstitutions);
          setNoInstitutions(orgInstitutions.length === 0);
          setIsLoadingInstitutions(false); // Institutions loaded from org
          console.log("Setting institutions from org:", orgInstitutions);
          
          // Handle current institution selection
          if (orgInstitutions.length > 0) {
            // Check if there's a saved institution in localStorage
            const savedInstitutionJson = localStorage.getItem('currentInstitution');
            let savedInstitution = null;
            
            if (savedInstitutionJson) {
              try {
                savedInstitution = JSON.parse(savedInstitutionJson);
              } catch (e) {
                console.error('Failed to parse saved institution:', e);
                localStorage.removeItem('currentInstitution');
              }
            }
            
            // Find valid institution to select
            let institutionToSelect = null;
            
            // First try saved institution
            if (savedInstitution) {
              institutionToSelect = orgInstitutions.find(inst => inst.id === savedInstitution.id);
            }
            
            // Then try current institution from backend
            if (!institutionToSelect && selectedOrg.currentInstitution) {
              institutionToSelect = orgInstitutions.find(inst => inst.id === selectedOrg.currentInstitution);
            }
            
            // Finally fallback to first available
            if (!institutionToSelect) {
              institutionToSelect = orgInstitutions[0];
            }
            
            if (institutionToSelect) {
              console.log('Auto-selecting institution:', institutionToSelect.name);
              setCurrentInstitution(institutionToSelect);
              localStorage.setItem('currentInstitution', JSON.stringify(institutionToSelect));
            }
          } else {
            // No institutions available
            setCurrentInstitution(null);
            localStorage.removeItem('currentInstitution');
          }
        } else {
          // No organizations available
          setOrganizations([]);
          setNoOrganizations(true);
          setCurrentOrganization(null);
          setError("You don't belong to any organizations. Please contact an administrator.");
        }
        
      } catch (err) {
        console.error('Failed to fetch organizations:', err);
        setError('Failed to load organizations');
        setNoOrganizations(true);
        setCurrentOrganization(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrganizations();
  }, [initialized, keycloak.authenticated]);

  const handleSetCurrentOrganization = async (org: Organization) => {
    console.log('Switching to organization:', org.name);
    
    const previousOrganization = currentOrganization;
    isFetched.current = false;
    _orgFetchInitiated = false;
    
    // Dispatch event to start loading overlay
    if (previousOrganization?.id !== org.id) {
      console.log('[ORGANIZATION] Dispatching organizationSwitchStart event');
      window.dispatchEvent(new CustomEvent('organizationSwitchStart', {
        detail: { organization: org, previousOrganization }
      }));
    }
    
    // Only call backend API if the organization actually changed
    if (previousOrganization?.id !== org.id) {
      try {
        console.log('[ORGANIZATION] Updating current organization on backend:', org.id, 'at', new Date().toISOString());
        const updateResult = await updateCurrentOrganization(org.id);
        
        if (!updateResult.success) {
          throw new Error(updateResult.error || 'Backend update failed');
        }
        
        console.log('[ORGANIZATION] Organization preference updated on backend:', org.name, 'at', new Date().toISOString());
        console.log('[ORGANIZATION] Backend response:', updateResult);
        
        // Refetch organizations from backend to get fresh data with new current organization
        console.log('[ORGANIZATION] Refetching organizations from backend to sync data...');
        const response = await fetchUserOrganizations();
        
        if (!response.success || !response.organizations) {
          throw new Error('Failed to refetch organizations after update');
        }
        
        const orgs = response.organizations;
        console.log('[ORGANIZATION] Refetched organizations:', orgs);
        
        // Update organizations list with fresh data from backend
        setOrganizations(orgs);
        
        // Find the organization we just switched to in the fresh data
        const updatedOrg = orgs.find((o: Organization) => o.id === org.id);
        if (!updatedOrg) {
          throw new Error('Organization not found in refetched data');
        }
        
        // Use the fresh organization data from backend
        org = updatedOrg;
        
      } catch (error) {
        console.error('Failed to update current organization on backend:', error);
        
        // Show error toast to user
        toast({
          variant: "destructive",
          title: "Failed to Update Organization",
          description: "Your selection has been saved locally, but we couldn't sync it with the server. Please try again later.",
        });
        
        // Dispatch event to end loading overlay on error
        window.dispatchEvent(new CustomEvent('organizationSwitchEnd', {
          detail: { organization: previousOrganization, previousOrganization, error: true }
        }));
        
        // Don't proceed with the switch if backend update failed
        return;
      }
    }
    
    // NOW update local state - AFTER backend has confirmed the change
    console.log('[ORGANIZATION] Backend confirmed, now updating local state');
    setCurrentOrganization(org);
    localStorage.setItem('currentOrganizationId', org.id);
    
    // Clear organization-specific caches AFTER backend update
    clearOrganizationCache();
    
    // Invalidate React Query caches for organization-specific data
    // Remove cached data without immediately refetching to prevent hooks errors
    queryClient.removeQueries(['institutions']);
    queryClient.removeQueries(['groups']);
    queryClient.removeQueries(['kids']);
    queryClient.removeQueries(['kids-sign-in']);
    queryClient.removeQueries(['teachers']);
    queryClient.removeQueries(['employees']);
    queryClient.removeQueries(['parents']);
    
    // Invalidate tasks data when organization changes
    queryClient.removeQueries(['tasks-list']);
    queryClient.removeQueries(['task-comments']);
    queryClient.invalidateQueries(['tasks-list']);
    
    // Also invalidate chat-related caches when organization changes
    queryClient.removeQueries(['chat-spaces']);
    queryClient.removeQueries(['chat-space']);
    queryClient.removeQueries(['chat-rooms']);
    queryClient.removeQueries(['chat-members']);
    queryClient.removeQueries(['chat-messages']);
    
    // Handle institutions from the new organization (now using fresh data from backend)
    const orgInstitutions = org.institutions || [];
    setInstitutions(orgInstitutions);
    setNoInstitutions(orgInstitutions.length === 0);
    
    // Handle institution selection for new organization
    if (orgInstitutions.length > 0) {
      // Try to use the organization's current institution from backend
      let institutionToSelect = null;
      if (org.currentInstitution) {
        institutionToSelect = orgInstitutions.find(inst => inst.id === org.currentInstitution);
      }
      
      // Fallback to first institution if no current institution set
      if (!institutionToSelect) {
        institutionToSelect = orgInstitutions[0];
      }
      
      if (institutionToSelect) {
        console.log('Auto-selecting institution for new org:', institutionToSelect.name);
        setCurrentInstitution(institutionToSelect);
        localStorage.setItem('currentInstitution', JSON.stringify(institutionToSelect));
      }
    } else {
      // No institutions in new organization
      setCurrentInstitution(null);
      localStorage.removeItem('currentInstitution');
    }
    
    // Dispatch event to end loading overlay
    if (previousOrganization?.id !== org.id) {
      console.log('[ORGANIZATION] Dispatching organizationSwitchEnd event');
      window.dispatchEvent(new CustomEvent('organizationSwitchEnd', {
        detail: { organization: org, previousOrganization }
      }));
    }
  };

  const handleSetCurrentInstitution = async (institution: Institution | null) => {
    console.log('OrganizationContext: handleSetCurrentInstitution called', {
      from: currentInstitution?.name || 'null',
      to: institution?.name || 'null'
    });
    
    // Prevent deselection if there are available institutions and user is trying to set null
    if (institution === null && institutions.length > 0 && currentInstitution) {
      console.warn('OrganizationContext: Preventing institution deselection - user must have an institution selected');
      return;
    }
    
    const previousInstitution = currentInstitution;
    
    // Dispatch event to start loading overlay
    if (previousInstitution?.id !== institution?.id) {
      console.log('[ORGANIZATION] Dispatching institutionSwitchStart event');
      window.dispatchEvent(new CustomEvent('institutionSwitchStart', {
        detail: { institution, previousInstitution }
      }));
    }
    
    // Only call backend API if the institution actually changed
    if (institution && previousInstitution?.id !== institution.id) {
      try {
        console.log('[ORGANIZATION] Updating current institution on backend:', institution.id, 'at', new Date().toISOString());
        const updateResult = await updateCurrentInstitution(institution.id);
        
        if (!updateResult.success) {
          throw new Error(updateResult.error || 'Backend update failed');
        }
        
        console.log('[ORGANIZATION] Institution preference updated on backend:', institution.name, 'at', new Date().toISOString());
        console.log('[ORGANIZATION] Backend response:', updateResult);
      } catch (error) {
        console.error('Failed to update current institution on backend:', error);
        
        // Show error toast to user
        toast({
          variant: "destructive",
          title: "Failed to Update Institution",
          description: "Your selection has been saved locally, but we couldn't sync it with the server. Please try again later.",
        });
        
        // Dispatch event to end loading overlay on error
        window.dispatchEvent(new CustomEvent('institutionSwitchEnd', {
          detail: { institution: previousInstitution, previousInstitution, error: true }
        }));
        
        // Don't proceed with the switch if backend update failed
        return;
      }
    }
    
    // NOW update local state - AFTER backend has confirmed the change
    console.log('[ORGANIZATION] Backend confirmed, now updating local state for institution');
    setCurrentInstitution(institution);
    
    if (institution) {
      localStorage.setItem('currentInstitution', JSON.stringify(institution));
    } else {
      localStorage.removeItem('currentInstitution');
    }
    
    // Invalidate caches after backend update is complete and state is updated
    console.log('[ORGANIZATION] Starting cache invalidation for institution:', institution?.id, 'at', new Date().toISOString());
    
    // Invalidate data caches when institution changes
    // This ensures fresh data is fetched for the new institution context
    queryClient.removeQueries(['chat-spaces']);
    queryClient.removeQueries(['chat-space']);
    queryClient.removeQueries(['chat-rooms']);
    queryClient.removeQueries(['chat-members']);
    queryClient.removeQueries(['chat-messages']);
    
    // Invalidate tasks data when institution changes
    queryClient.removeQueries(['tasks-list']);
    queryClient.removeQueries(['task-comments']);
    queryClient.invalidateQueries(['tasks-list']);
    
    // Invalidate staff/employees data when institution changes
    queryClient.invalidateQueries(['staff']);
    queryClient.invalidateQueries(['employees']);
    
    // Clear groups cache and invalidate queries to force fresh data
    clearGroupsCache();
    queryClient.invalidateQueries(['groups']);
    console.log('[ORGANIZATION] Cache invalidation completed for institution:', institution?.id, 'at', new Date().toISOString());
    
    // Dispatch a custom event to notify components about the institution change
    const institutionChangedEvent = new CustomEvent('institutionChanged', {
      detail: { institution }
    });
    window.dispatchEvent(institutionChangedEvent);
    
    // Dispatch event to end loading overlay
    if (previousInstitution?.id !== institution?.id) {
      console.log('[ORGANIZATION] Dispatching institutionSwitchEnd event');
      window.dispatchEvent(new CustomEvent('institutionSwitchEnd', {
        detail: { institution, previousInstitution }
      }));
    }
  };

  return (
    <OrganizationContext.Provider
      value={{
        organizations,
        currentOrganization,
        currentInstitution,
        institutions,
        isLoading,
        isLoadingInstitutions,
        error,
        noOrganizations,
        noInstitutions,
        setCurrentOrganization: handleSetCurrentOrganization,
        setCurrentInstitution: handleSetCurrentInstitution,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}; 